// API Security — Discovery (replaces Catalog).
// Matches the user's reference mock: counts row → search → table with
// checkbox + source(host) + activity(method+path) + status pill + labels
// + per-row Save/Ignore actions, plus bulk select-all.
import React from "react";
import {
  Stack, Box, Typography, Button, Checkbox, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
  Select, MenuItem, CircularProgress, InputAdornment, TablePagination,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Filter } from "lucide-react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import RouteIcon from "@mui/icons-material/AltRoute";

import asec from "../../../../../utils/axios/v1/asecAxios";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import { useNotification } from "../../../../../contexts/NotificationContext";
import AsecPage, { AsecPanel, SubLabel } from "./AsecPage";
import SectionHelp from "./components/SectionHelp";
import EndpointDetailDrawer from "./components/EndpointDetailDrawer";
import useAsecPermissions from "./useAsecPermissions";
import LayerdIcon from "../../../../common/LayerdIcon";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import {
  asecBtnOutlinedSm,
  asecBtnTextSm,
  asecTablePaperSx,
  asecSettingsTableHeadSx,
  asecSettingsTableBodySx,
  asecMonoBlueSx,
  asecFindingsTableSx,
  EVENT_FILTER_BAR_SX,
  filterInputSx,
  filterSelectSx,
  asecCheckboxSx,
  asecOverviewMetricCardSx,
} from "./asecUi";
import { SafeTextField } from "../../../../common/Greens";
import WAFOverviewMetricCard, {
  WAFOverviewIconChip,
  WAFOverviewStatNumber,
  wafOverviewGridSx,
} from "../web_application/components/WAFOverviewMetricCard";

// 6 status counters at the top of the page. The DB column
// review_state still uses the legacy `saved` value (kept for
// backward compatibility with bulk-review tooling); the UI just
// labels it "Reviewed" so it matches the "Mark reviewed" button.
// Same pattern for "Ignored" — the label and the action verb match.
const COUNTER_TILES = [
  { key: "total", label: "Discovered", Icon: RouteIcon },
  { key: "needs_review", label: "Needs Review", Icon: FlagOutlinedIcon },
  { key: "saved", label: "Reviewed", Icon: CheckCircleOutlineIcon },
  { key: "ignored", label: "Ignored", Icon: VisibilityOffOutlinedIcon },
  { key: "label_review", label: "Label Review", Icon: LabelOutlinedIcon },
  { key: "hostnames", label: "Hostname", Icon: LanguageOutlinedIcon },
];

// User-friendly verb per review_state, used by the success toast.
// "saved" is the historical DB value for what the UI calls
// "reviewed" — `prettyReview('saved') === 'marked reviewed'`.
const PRETTY_REVIEW = {
  saved:        "marked reviewed",
  ignored:      "ignored",
  needs_review: "moved back to needs-review",
  label_review: "queued for label review",
};
function prettyReview(s) { return PRETTY_REVIEW[s] || s; }

const discoveryTableHeadSx = {
  ...asecSettingsTableHeadSx,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: colors.neutral[700],
  borderBottom: `1px solid ${colors.neutral[200]}`,
  backgroundColor: colors.neutral[50],
  py: "11px",
  whiteSpace: "nowrap",
};

const discoveryTableSx = {
  ...asecFindingsTableSx,
  width: "100%",
  tableLayout: "fixed",
};

const discoveryDataColSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const discoveryPillSx = {
  height: 24,
  fontSize: 12,
  fontWeight: 600,
  borderRadius: "6px",
  "& .MuiChip-label": { px: 1.25, lineHeight: "25px" },
};

function StatusPill({ state }) {
  const sx = {
    needs_review: { bg: "rgba(245, 158, 11, 0.10)", fg: "#b45309", bd: "rgba(245, 158, 11, 0.28)" },
    saved:        { bg: "rgba(16, 185, 129, 0.10)", fg: "#0f766e", bd: "rgba(16, 185, 129, 0.28)" },
    ignored:      { bg: "rgba(107, 114, 128, 0.10)", fg: "#374151", bd: "rgba(107, 114, 128, 0.22)" },
    label_review: { bg: "rgba(99, 113, 247, 0.10)", fg: colors.primary[500] || "#4338ca", bd: "rgba(99, 113, 247, 0.24)" },
  }[state] || { bg: colors.neutral[100], fg: colors.neutral[800], bd: colors.neutral[200] };
  const label = (state || "needs_review").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        ...discoveryPillSx,
        backgroundColor: sx.bg,
        color: sx.fg,
        border: `1px solid ${sx.bd}`,
      }}
    />
  );
}

function MethodPill({ method }) {
  const sx = {
    GET:    { bg: "rgba(16, 185, 129, 0.10)", fg: "#0f766e" },
    POST:   { bg: "rgba(99, 113, 247, 0.10)", fg: colors.primary[500] || "#4338ca" },
    PUT:    { bg: "rgba(245, 158, 11, 0.10)", fg: "#b45309" },
    PATCH:  { bg: "rgba(245, 158, 11, 0.10)", fg: "#b45309" },
    DELETE: { bg: "rgba(239, 68, 68, 0.10)", fg: "#b91c1c" },
  }[method] || { bg: colors.neutral[200], fg: colors.neutral[800] };
  return <Chip label={method} size="small" sx={{ backgroundColor: sx.bg, color: sx.fg, fontSize: 12, fontWeight: "medium", minWidth: 64 }} />;
}

// Spec-coverage axis — orthogonal to the curation `review_state`
// column. An endpoint is `documented` when its `spec_id` is set
// (the drift_detector cron auto-links matches to the active spec).
// `shadow` rows have a matching open `spec_drift` row of
// kind=shadow — meaning the synthesizer saw traffic for an
// endpoint the active spec doesn't declare. `mismatch` is an
// additional warning on documented rows: the spec declares the
// path, but the catalog observed query keys the spec doesn't
// declare.
function classifySpecCoverage(row, driftByKey, hasActiveSpec) {
  if (!hasActiveSpec) return "no_spec";
  const key = `${row.host}|${row.method}|${row.path_template}`;
  const drift = driftByKey[key];
  if (row.spec_id) {
    if (drift && drift.kind === "mismatch") return "mismatch";
    return "documented";
  }
  return "shadow";
}

function SpecCoverageChip({ coverage }) {
  const meta = {
    documented: { label: "Documented", bg: "rgba(16, 185, 129, 0.10)", fg: "#0f766e", bd: "rgba(16, 185, 129, 0.28)",
                  icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> },
    shadow:     { label: "Shadow",     bg: "rgba(245, 158, 11, 0.10)", fg: "#b45309", bd: "rgba(245, 158, 11, 0.28)",
                  icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
    mismatch:   { label: "Mismatch",   bg: "rgba(99, 113, 247, 0.10)", fg: "#4338ca", bd: "rgba(99, 113, 247, 0.24)",
                  icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
    no_spec:    { label: "No spec",    bg: colors.neutral[100], fg: colors.neutral[700], bd: colors.neutral[200],
                  icon: null },
  }[coverage];
  if (!meta) {
    return <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>—</Typography>;
  }
  return (
    <Chip
      label={(
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          {meta.icon}
          <span>{meta.label}</span>
        </Box>
      )}
      size="small"
      sx={{
        ...discoveryPillSx,
        backgroundColor: meta.bg,
        color: meta.fg,
        border: `1px solid ${meta.bd}`,
      }}
    />
  );
}

// Keyframe applied to a tile briefly after a save/ignore action so
// the operator's eye is drawn to where the row went. Pure CSS,
// scoped via MUI sx — no animation library needed.
const pulseSx = {
  "@keyframes asecCounterPulse": {
    "0%":   { boxShadow: "0 0 0 0 rgba(99, 113, 247, 0.45)" },
    "60%":  { boxShadow: "0 0 0 10px rgba(99, 113, 247, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(99, 113, 247, 0)" },
  },
  animation: "asecCounterPulse 1.5s ease-out",
  borderColor: `${colors.primary[300]} !important`,
};

function CounterTile({ tile, value, active, onClick, pulse }) {
  const { label, Icon } = tile;
  const displayValue = typeof value === "number" ? value.toLocaleString() : (value ?? "—");
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        height: "100%",
        ...(pulse ? pulseSx : {}),
      }}
    >
      <WAFOverviewMetricCard
        highlight={active}
        icon={(
          <WAFOverviewIconChip variant="primary">
            <Icon sx={{ fontSize: 16 }} />
          </WAFOverviewIconChip>
        )}
        label={label}
        sx={{
          ...asecOverviewMetricCardSx,
          height: "100%",
          transition: "border-color 120ms, box-shadow 120ms",
          "&:hover": onClick ? { boxShadow: "0 2px 8px rgba(17, 17, 17, 0.06)" } : undefined,
        }}
      >
        <WAFOverviewStatNumber value={displayValue} />
      </WAFOverviewMetricCard>
    </Box>
  );
}

// Allowed page sizes for the MUI TablePagination control. 25 is the
// default — large enough to fill a typical viewport, small enough that
// the per-row checkboxes don't get lost in a long list. 100 caps at a
// reasonable scroll length; the API supports up to 500 if a power-user
// hits the URL directly.
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
// Debounce window for the search input. Empirically tuned: 250 ms is
// short enough that typing feels responsive, long enough that the
// API isn't hit on every keystroke for a fast typist.
const SEARCH_DEBOUNCE_MS = 250;

function truncatePath(path, max = 56) {
  if (!path) return "—";
  return path.length > max ? `${path.slice(0, max)}…` : path;
}

function DiscoveryFilterBar({
  search,
  onSearchChange,
  hostFilter,
  onHostFilterChange,
  hostList,
  specFilter,
  onSpecFilterChange,
  coverageCounts,
  hasActiveSpec,
  pendingCandidateId,
  loading,
  refreshing,
  onRefresh,
  selected,
  canConfigure,
  onMarkReviewed,
  onIgnoreSelected,
}) {
  const hasActiveFilters = Boolean(
    search || hostFilter || (hasActiveSpec && specFilter !== "all")
  );

  const clearFilters = () => {
    onSearchChange("");
    onHostFilterChange("");
    if (hasActiveSpec) onSpecFilterChange("all");
  };

  const fmtCount = (n) => (typeof n === "number" ? n.toLocaleString() : "—");

  return (
    <Box sx={EVENT_FILTER_BAR_SX}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ gap: 1.5, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LayerdIcon icon={Filter} size={18} color="primary" />
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
              Filters
            </Typography>
          </Stack>

          <SafeTextField
            placeholder="Search path, host or method…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ ...filterInputSx, minWidth: 200, maxWidth: 320, flex: "1 1 200px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: colors.neutral[500] }} />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={hostFilter || "all"}
            onChange={(e) => {
              const v = e.target.value;
              onHostFilterChange(v === "all" ? "" : v);
            }}
            sx={{ ...filterSelectSx, minWidth: 180 }}
          >
            <MenuItem value="all">All hostnames</MenuItem>
            {hostList.map((h) => (
              <MenuItem key={h} value={h}>{h}</MenuItem>
            ))}
          </Select>

          {hasActiveSpec ? (
            <Select
              value={specFilter}
              onChange={(e) => onSpecFilterChange(e.target.value)}
              sx={{ ...filterSelectSx, minWidth: 200 }}
            >
              <MenuItem value="all">All coverage ({fmtCount(coverageCounts.all)})</MenuItem>
              <MenuItem value="documented">Documented ({fmtCount(coverageCounts.documented)})</MenuItem>
              <MenuItem value="shadow">Shadow ({fmtCount(coverageCounts.shadow)})</MenuItem>
              <MenuItem value="mismatch">Mismatch ({fmtCount(coverageCounts.mismatch)})</MenuItem>
            </Select>
          ) : null}

          {hasActiveFilters ? (
            <Chip
              label="Clear all"
              size="small"
              onClick={clearFilters}
              sx={{
                height: 36,
                fontSize: 14,
                borderRadius: "8px",
                backgroundColor: "#fff",
                border: `1px solid ${colors.neutral[200]}`,
                color: colors.neutral[800],
                "&:hover": {
                  borderColor: colors.red[100],
                  color: colors.red[100],
                },
              }}
            />
          ) : null}

          {pendingCandidateId && coverageCounts.shadow > 0 ? (
            <Tooltip title="A draft spec covering observed traffic is pending — review the whole draft from the Schema Validation page.">
              <Typography sx={{ fontSize: 12, color: colors.primary[500] || "#4338ca" }}>
                Candidate spec available
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
          {canConfigure && selected.size > 0 ? (
            <>
              <Typography sx={{ fontSize: 14, color: colors.neutral[700] }}>
                {selected.size} selected
              </Typography>
              <Button variant="outlined" size="small" onClick={onMarkReviewed} sx={asecBtnOutlinedSm}>
                Mark reviewed
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={onIgnoreSelected}
                sx={{
                  ...asecBtnOutlinedSm,
                  color: colors.yellow[200],
                  borderColor: colors.yellow[200],
                  "&:hover": {
                    borderColor: colors.yellow[100],
                    color: colors.yellow[100],
                    backgroundColor: colors.yellow[10],
                  },
                }}
              >
                Ignore selected
              </Button>
            </>
          ) : null}
          <RefreshIconButton
            onClick={onRefresh}
            disabled={refreshing || loading}
            loading={refreshing}
            size="compact"
          />
        </Stack>
      </Stack>
    </Box>
  );
}

// Mismatch details dialog — opened from the warning icon on a
// documented row. Renders the drift row's `detail` payload, which
// the drift_detector cron builds with this exact shape:
//   { endpoint_id, spec_endpoint_id, undeclared_query_keys: [...] }
// "Mark resolved" closes the loop without waiting for the next cron
// tick (the cron will re-open it if the condition still holds).
function MismatchDetailDialog({ open, info, onClose, onResolve }) {
  if (!info) return null;
  const detail = info.drift?.detail || {};
  const keys = Array.isArray(detail.undeclared_query_keys)
    ? detail.undeclared_query_keys
    : [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
fontSize: 16, fontWeight: 600, pr: 1,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <WarningAmberIcon sx={{ color: "#4338ca", fontSize: 16 }} />
          <span>Mismatch detail</span>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography sx={ { fontSize: 12, color: colors.neutral[600], mb: 0.5 }}>
              Endpoint
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <MethodPill method={info.method} />
              <Typography sx={{ fontSize: 14, color: colors.neutral[1000] }}>
                {info.path_template}
              </Typography>
            </Stack>
            <Typography sx={ { fontSize: 12, color: colors.neutral[700], mt: 0.5 }}>
              {info.host}
            </Typography>
          </Box>

          <Box>
            <Typography sx={ { fontSize: 12, color: colors.neutral[600], mb: 0.5 }}>
              Undeclared query parameters ({keys.length})
            </Typography>
            {keys.length === 0 ? (
              <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                No specific keys reported — the drift detector flagged this row but didn't
                attach a key list (older cron build).
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" spacing={0.75} sx={{ rowGap: 0.75 }}>
                {keys.map((k) => (
                  <Chip
                    key={k}
                    label={k}
                    size="small"
                    sx={{
                      fontSize: 12,
                      backgroundColor: "rgba(99, 113, 247, 0.10)",
                      color: "#4338ca",
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {info.drift?.detected_at && (
            <Box>
              <Typography sx={ { fontSize: 12, color: colors.neutral[600], mb: 0.5 }}>
                First detected
              </Typography>
              <Typography sx={ { fontSize: 14, color: colors.neutral[1000] }}>
                {formatDate(info.drift.detected_at)}
              </Typography>
            </Box>
          )}

          <Box sx={{
            mt: 1, p: 1.5, borderRadius: "6px",
            backgroundColor: colors.neutral[100],
            border: `1px solid ${colors.neutral[200]}`,
          }}>
            <Typography sx={ { fontSize: 12, color: colors.neutral[700] }}>
              <strong>Suggested action:</strong> add these query parameters to the active
              spec's operation (so the validator accepts them), or strip them upstream
              before they reach this endpoint. Resolving this row stops the warning until
              the drift detector sees the condition again.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={ { textTransform: "none" }}>
          Close
        </Button>
        <Button
          variant="contained" color="primary"
          onClick={() => onResolve(info.drift?.id)}
          disabled={!info.drift?.id}
          sx={ { textTransform: "none" }}
        >
          Mark resolved
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Body({ siteId }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure, canWrite } = useAsecPermissions();
  const [counts, setCounts] = React.useState(null);
  const [items, setItems] = React.useState(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [filter, setFilter] = React.useState("needs_review"); // counter tile filter
  // Spec-coverage filter — independent of `filter` (review_state).
  // Server-side: the CP `/endpoints` route honours `spec_coverage`
  // and returns site-wide `coverage_counts` so pagination is
  // correct across all matching rows (otherwise "Shadow" would
  // only show the current page's shadow rows).
  const [specFilter, setSpecFilter] = React.useState("all");
  const [coverageCounts, setCoverageCounts] = React.useState({
    all: 0, documented: 0, shadow: 0, mismatch: 0,
  });
  // Indexes built each reload from /spec/drift (open shadow + mismatch
  // — full rows, not just kinds, so the mismatch drawer can show
  // detail.undeclared_query_keys) and /spec/candidates/:id/endpoints.
  // Keyed by `host|method|path_template`.
  const [driftByKey, setDriftByKey] = React.useState({});
  const [candEpByKey, setCandEpByKey] = React.useState({});
  const [hasActiveSpec, setHasActiveSpec] = React.useState(false);
  // Spec validation posture — `policy.closed_world=true` means
  // Layerd 404s requests for paths not declared in the active
  // spec. We surface a warning when an operator has reviewed a
  // shadow row but not yet promoted it, because closed-world
  // means that endpoint's real traffic is being blocked.
  const [specPolicy, setSpecPolicy] = React.useState({
    mode: "off", closed_world: false,
  });
  const [pendingCandidateId, setPendingCandidateId] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  // Mismatch details drawer state. `null` when closed; otherwise
  // an object with the host/method/path_template + the matching
  // spec_drift row's detail blob.
  const [mismatchDetail, setMismatchDetail] = React.useState(null);
  // Counter-tile key to pulse briefly after a save/ignore action,
  // so the operator's eye tracks where the row went. Cleared after
  // ~1.5s by the effect below.
  const [pulseKey, setPulseKey] = React.useState(null);
  React.useEffect(() => {
    if (!pulseKey) return undefined;
    const h = setTimeout(() => setPulseKey(null), 1500);
    return () => clearTimeout(h);
  }, [pulseKey]);
  // Detail drawer state. `drawerRow` is the full enriched row
  // (with _coverage) currently rendered; null when closed.
  const [drawerRow, setDrawerRow] = React.useState(null);
  const [search, setSearch] = React.useState("");
  // `query` is the debounced version of `search`. Only `query` triggers
  // a reload; `search` is the immediate textbox value so typing feels
  // snappy without an API call per keystroke.
  const [query, setQuery] = React.useState("");
  const [hostFilter, setHostFilter] = React.useState("");
  const [selected, setSelected] = React.useState(() => new Set());
  const [hostList, setHostList] = React.useState([]);

  // Debounce `search` → `query`. Cleared on unmount + on rapid typing.
  React.useEffect(() => {
    const h = setTimeout(() => setQuery(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [search]);

  // Any filter change resets to page 0 — otherwise switching a counter
  // tile can leave you stranded on a page that no longer exists.
  React.useEffect(() => { setPage(0); }, [filter, hostFilter, query, specFilter]);

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    setRefreshing(true);
    setSelected(new Set());
    try {
      // Endpoints + counters are the source of truth for the table
      // body and the counter tiles. Drift + active-spec + pending-
      // candidate are *enrichment* — we still render fine when any
      // of those 404s or 500s (catch → empty index).
      const [c, e, drift, active, candList, policy] = await Promise.all([
        asec.endpointCounts(siteId).catch(() => ({ data: {} })),
        asec.endpoints(siteId, {
          page,
          limit: pageSize,
          ...(filter && filter !== "total" && filter !== "hostnames" ? { review_state: filter } : {}),
          ...(hostFilter ? { host: hostFilter } : {}),
          ...(query ? { q: query } : {}),
          ...(specFilter && specFilter !== "all" ? { spec_coverage: specFilter } : {}),
        }),
        asec.driftList(siteId, { open: "true", limit: 500 }).catch(() => ({ data: [] })),
        asec.specActive(siteId).catch(() => ({ data: { active: null } })),
        asec.candidateList(siteId, { status: "pending", limit: 1 }).catch(() => ({ data: [] })),
        asec.specPolicy(siteId).catch(() => ({ data: { mode: "off", closed_world: false } })),
      ]);
      setCounts(c.data || {});
      const rows = Array.isArray(e.data?.items) ? e.data.items : [];
      setItems(rows);
      setTotal(typeof e.data?.total === "number" ? e.data.total : rows.length);
      // Site-wide coverage counts — preserved across reload calls
      // even when items is filtered server-side. When the API
      // doesn't return them (older CP), default to zeros and let
      // the chip-bar render with "-" for counts.
      if (e.data?.coverage_counts) {
        setCoverageCounts({
          all: Number(e.data.coverage_counts.all || 0),
          documented: Number(e.data.coverage_counts.documented || 0),
          shadow: Number(e.data.coverage_counts.shadow || 0),
          mismatch: Number(e.data.coverage_counts.mismatch || 0),
        });
      }
      // Store FULL drift rows (not just kinds) so the mismatch
      // drawer can read `detail.undeclared_query_keys`. Mismatch
      // wins over shadow when both exist for one key.
      const dByKey = {};
      for (const d of drift.data || []) {
        const k = `${d.host}|${d.method}|${d.path_template}`;
        if (d.kind === "mismatch") dByKey[k] = d;
        else if (!dByKey[k]) dByKey[k] = d;
      }
      setDriftByKey(dByKey);
      setHasActiveSpec(!!active.data?.spec_id);
      setSpecPolicy({
        mode: policy.data?.mode || "off",
        closed_world: !!policy.data?.closed_world,
      });
      // Pull the pending candidate's endpoints so we can render the
      // "Add to spec" button only when there's actually a candidate-
      // endpoint to graft. No pending candidate → empty index → no
      // buttons (operator goes to SchemaValidation to wait for the
      // synthesizer's next tick).
      const cand = Array.isArray(candList.data) && candList.data[0];
      if (cand) {
        setPendingCandidateId(cand.id);
        const eps = await asec.candidateEndpoints(siteId, cand.id).catch(() => ({ data: [] }));
        const m = {};
        for (const ep of eps.data || []) {
          m[`${ep.host}|${ep.method}|${ep.path_template}`] = ep.id;
        }
        setCandEpByKey(m);
      } else {
        setPendingCandidateId(null);
        setCandEpByKey({});
      }
    } catch (err) {
      showError(err?.message || String(err));
      setItems([]);
      setTotal(0);
    } finally {
      setRefreshing(false);
    }
  }, [siteId, page, pageSize, filter, hostFilter, query, specFilter, showError]);
  React.useEffect(() => { reload(); }, [reload]);

  // Promote one candidate-endpoint into the active spec — the
  // surgical "this shadow row should be in my spec" action. Calls
  // the CP per-endpoint route which copies into spec_endpoints +
  // spec_schemas under spec_active.spec_id and auto-links the
  // catalog row's spec_id (so status_classifier marks it
  // `documented` on its next tick). Layerd reloads via SPEC_UPDATE
  // pg_notify within ~2s. `spec_active` itself is not touched.
  const resolveDriftRow = async (driftId) => {
    if (!driftId) return;
    try {
      await asec.driftResolve(siteId, driftId);
      showSuccess("Drift marked resolved");
      setMismatchDetail(null);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || "resolve failed");
    }
  };

  const promoteShadow = async (candidateEndpointId, host, method, path) => {
    if (!canWrite || !candidateEndpointId) return;
    setBusy(candidateEndpointId);
    try {
      await asec.candidateEndpointPromote(siteId, candidateEndpointId);
      showSuccess(`Added ${method} ${path} to active spec`);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || `promote failed for ${method} ${path}`);
    } finally {
      setBusy(null);
    }
  };

  // Hosts for the dropdown come from a separate page-1 unfiltered fetch
  // (api_only=true, no review_state filter) — derived from `items`
  // would only show hosts present on the current page. Fetched once
  // per site change, not on every page flip.
  React.useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await asec.endpoints(siteId, { limit: 500, page: 0 });
        if (cancelled) return;
        const set = new Set();
        for (const it of r.data?.items || []) if (it.host) set.add(it.host);
        setHostList(Array.from(set).sort());
      } catch { /* dropdown stays whatever it was */ }
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  // Per-row coverage labeling stays client-side (the row's chip
  // needs to render even when the *filter* is "all"). But the
  // counts in the chip-bar come from the server (site-wide,
  // ignoring all filters except api_only + status≠zombie/deprecated),
  // and the filter itself is server-side — `visibleItems` is just
  // the items the server returned for the current page.
  const itemsWithCoverage = React.useMemo(() => {
    const list = items || [];
    return list.map((r) => ({
      _coverage: classifySpecCoverage(r, driftByKey, hasActiveSpec),
      ...r,
    }));
  }, [items, driftByKey, hasActiveSpec]);

  const visibleItems = itemsWithCoverage;

  // Server is the source of truth for filtering now — the table just
  // renders `items` directly. Selection-tracking still needs to know
  // which rows are visible to compute the bulk header checkbox.
  const allSelected = visibleItems && visibleItems.length > 0 && visibleItems.every((r) => selected.has(r.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) (visibleItems || []).forEach((r) => next.delete(r.id));
    else (visibleItems || []).forEach((r) => next.add(r.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const setReviewState = async (ids, review_state) => {
    if (!canConfigure || ids.length === 0) return;
    try {
      if (ids.length === 1) {
        await asec.endpointPatch(siteId, ids[0], { review_state });
      } else {
        await asec.endpointBulk(siteId, { ids, review_state });
      }
      const verb = prettyReview(review_state);
      const destinationTab = review_state === "saved" ? "Reviewed"
                            : review_state === "ignored" ? "Ignored"
                            : review_state === "label_review" ? "Label Review"
                            : null;
      const tabHint = destinationTab ? ` — see the “${destinationTab}” tile to view it` : "";
      showSuccess(
        ids.length === 1
          ? `Endpoint ${verb}${tabHint}`
          : `${ids.length} endpoints ${verb}${tabHint}`
      );
      // Pulse the destination counter tile so the operator sees
      // where the row just went — closes the "wait, where did it
      // disappear to?" loop without forcing them to read the toast.
      if (review_state === "saved") setPulseKey("saved");
      else if (review_state === "ignored") setPulseKey("ignored");
      else if (review_state === "label_review") setPulseKey("label_review");
      else setPulseKey(null);
      reload();
    } catch (e) { showError(e?.message || String(e)); }
  };

  return (
    <Stack spacing={4}>
      {/* Counter tiles row — header */}
      <Stack direction="row" alignItems="center" sx={{ mb: -1 }}>
        <Typography sx={ { fontSize: 12, fontWeight: 500, color: colors.neutral[700] }}>
          Curation state
        </Typography>
        <SectionHelp
          size="small"
          tooltip="Each tile is a filter for the table below. Default view is Needs Review — your queue of untriaged endpoints. Click a tile to switch view. The tile that lights up briefly after an action is where the row you just acted on went."
        />
      </Stack>

      {/* Counters row — WAF overview-style metric cards */}
      <Box sx={{
        ...wafOverviewGridSx,
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          lg: "repeat(6, 1fr)",
        },
      }}>
        {COUNTER_TILES.map((t) => (
          <CounterTile
            key={t.key}
            tile={t}
            value={counts?.[t.key] ?? "—"}
            active={filter === t.key}
            pulse={pulseKey === t.key}
            onClick={() => {
              if (t.key === "hostnames") return;
              if (t.key === "total") return setFilter("total");
              setFilter(t.key);
            }}
          />
        ))}
      </Box>

      {/* Filter explainer — first-time operators don't realise the
          table is filtered by review_state. Click-through tells
          them what each tile does without making them read docs. */}
      <Typography sx={{
fontSize: 12, color: colors.neutral[600],
        mt: -2, ml: 0.5,
      }}>
        Showing endpoints with status <strong>{(
          COUNTER_TILES.find((t) => t.key === filter)?.label || filter
        ).toLowerCase()}</strong>. Click any tile above to switch view —
        rows you mark reviewed move to the <strong>Reviewed</strong> tile;
        rows you ignore move to <strong>Ignored</strong>.
      </Typography>

      {/* Closed-world conflict banner. Fires when the site has
          spec validation in closed_world mode AND at least one
          row visible on this page is reviewed-but-shadow (i.e.,
          the operator triaged it but didn't add to spec — and
          Layerd is 404'ing real traffic for it). Only renders
          when actually relevant so it doesn't become wallpaper. */}
      {hasActiveSpec && specPolicy.closed_world &&
       itemsWithCoverage.some((r) => r._coverage === "shadow" && r.review_state === "saved") && (
        <Box sx={{
          borderRadius: "8px", p: 2,
          border: "1px solid rgba(220, 38, 38, 0.30)",
          backgroundColor: "rgba(220, 38, 38, 0.06)",
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <WarningAmberIcon sx={{ color: "#b91c1c", fontSize: 20 }} />
            <Stack>
              <Typography sx={ { fontSize: 14, color: "#b91c1c", fontWeight: 600 }}>
                Closed-world is enabled — reviewed endpoints aren't documented
              </Typography>
              <Typography sx={ { fontSize: 12, color: colors.neutral[700] }}>
                Layerd is 404-ing real traffic for endpoints that have been marked reviewed
                but not yet added to the active spec. Click "Add to spec" on the conflicting
                rows below, or disable closed-world in Schema Validation if you want lenient mode.
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}

      <DiscoveryFilterBar
        search={search}
        onSearchChange={setSearch}
        hostFilter={hostFilter}
        onHostFilterChange={setHostFilter}
        hostList={hostList}
        specFilter={specFilter}
        onSpecFilterChange={setSpecFilter}
        coverageCounts={coverageCounts}
        hasActiveSpec={hasActiveSpec}
        pendingCandidateId={pendingCandidateId}
        loading={items === null}
        refreshing={refreshing}
        onRefresh={reload}
        selected={selected}
        canConfigure={canConfigure}
        onMarkReviewed={() => setReviewState([...selected], "saved")}
        onIgnoreSelected={() => setReviewState([...selected], "ignored")}
      />

      {/* Table */}
      {items === null ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : visibleItems.length === 0 ? (
        <AsecPanel sx={{ maxWidth: "100%" }}>
          <SubLabel>No endpoints match the current filters.</SubLabel>
        </AsecPanel>
      ) : (
        <TableContainer component={Paper} sx={{ ...asecTablePaperSx, width: "100%" }}>
          <Table size="small" sx={discoveryTableSx}>
            <colgroup>
              <col style={{ width: 48 }} />
              <col />
              <col />
              <col />
              {hasActiveSpec && <col />}
              <col />
              <col />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={discoveryTableHeadSx}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={!allSelected && selected.size > 0}
                    onChange={toggleAll}
                    sx={asecCheckboxSx}
                  />
                </TableCell>
                <TableCell sx={{ ...discoveryTableHeadSx, ...discoveryDataColSx }}>Source</TableCell>
                <TableCell sx={{ ...discoveryTableHeadSx, ...discoveryDataColSx }}>Activity</TableCell>
                <TableCell sx={{ ...discoveryTableHeadSx, ...discoveryDataColSx }}>Status</TableCell>
                {hasActiveSpec && (
                  <TableCell sx={{ ...discoveryTableHeadSx, ...discoveryDataColSx }}>Spec</TableCell>
                )}
                <TableCell sx={{ ...discoveryTableHeadSx, ...discoveryDataColSx }}>Labels</TableCell>
                <TableCell sx={{ ...discoveryTableHeadSx, ...discoveryDataColSx }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={asecSettingsTableBodySx}>
              {visibleItems.map((r) => {
                const key = `${r.host}|${r.method}|${r.path_template}`;
                const candEpId = candEpByKey[key];
                const isShadow = r._coverage === "shadow";
                const isMismatch = r._coverage === "mismatch";
                return (
                <TableRow
                  key={r.id} hover selected={selected.has(r.id)}
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    // Only open the drawer when the click is on the
                    // row body itself — not on the checkbox or one
                    // of the per-row action buttons / icon buttons.
                    // MUI rows propagate; we look at the actual
                    // target to decide whether to swallow.
                    const tag = e.target.closest("button, input, a, [role='button']");
                    if (tag) return;
                    setDrawerRow(r);
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                      sx={asecCheckboxSx}
                    />
                  </TableCell>
                  <TableCell sx={{ ...asecSettingsTableBodySx, ...discoveryDataColSx, verticalAlign: "middle", py: 1.5 }}>
                    {r.host || "—"}
                  </TableCell>
                  <TableCell sx={{ ...discoveryDataColSx, verticalAlign: "middle", py: 1.5 }}>
                    <Stack spacing={0.25}>
                      <Typography sx={asecSettingsTableBodySx}>
                        {r.method || "—"}
                      </Typography>
                      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                        {r.path_template?.length > 56 ? (
                          <Tooltip title={r.path_template} arrow>
                            <Typography sx={{ ...asecMonoBlueSx, lineHeight: "25px", cursor: "default" }}>
                              {truncatePath(r.path_template)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ ...asecMonoBlueSx, lineHeight: "25px" }}>
                            {r.path_template || "—"}
                          </Typography>
                        )}
                        {isMismatch && (
                          <Tooltip title="Active spec declares this path, but the catalog observed query keys the spec doesn't declare. Click for details.">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMismatchDetail({
                                  host: r.host, method: r.method, path_template: r.path_template,
                                  drift: driftByKey[key],
                                });
                              }}
                              sx={{ p: 0, mt: "-2px" }}
                            >
                              <WarningAmberIcon sx={{ fontSize: 16, color: "#4338ca" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ ...discoveryDataColSx, verticalAlign: "middle", py: 1.5 }}>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <StatusPill state={r.review_state || "needs_review"} />
                      {r.reviewed_by && (
                        <Tooltip title={`Reviewed by ${r.reviewed_by}${r.reviewed_at ? " on " + formatDate(r.reviewed_at) : ""}`}>
                          <Typography sx={{
fontSize: 12, color: colors.neutral[600],
                            cursor: "default",
                          }}>
                            by {r.reviewed_by.split("@")[0]}
                          </Typography>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  {hasActiveSpec && (
                    <TableCell sx={{ ...discoveryDataColSx, verticalAlign: "middle", py: 1.5 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <SpecCoverageChip coverage={r._coverage} />
                        {isShadow && r.review_state === "saved" && specPolicy.closed_world && (
                          <Tooltip title="Closed-world is enabled — Layerd is currently 404-ing this endpoint because it isn't in the active spec. Click 'Add to spec' to include it, or disable closed-world in Schema Validation.">
                            <Chip
                              label="404'd"
                              size="small"
                              sx={{
fontSize: 12, height: 18,
                                backgroundColor: "rgba(220, 38, 38, 0.10)",
                                color: "#b91c1c", fontWeight: 600,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell sx={discoveryDataColSx}>
                    {(r.sensitivity_tags || []).length === 0 ? <SubLabel>—</SubLabel> :
                      r.sensitivity_tags.map((t, i) => (
                        <Chip key={i} label={t} size="small" sx={{ mr: 0.5, fontSize: 12, backgroundColor: colors.neutral[200] }} />
                      ))
                    }
                  </TableCell>
                  <TableCell align="left" sx={{ ...discoveryDataColSx, verticalAlign: "middle", py: 1.25 }}>
                    <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center" flexWrap="wrap" useFlexGap>
                      {isShadow && canWrite && (
                        candEpId ? (
                          <Button
                            size="small"
                            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                            disabled={busy === candEpId}
                            onClick={() => promoteShadow(candEpId, r.host, r.method, r.path_template)}
                            sx={{ ...asecBtnTextSm, color: "#0f766e", "&:hover": { backgroundColor: "rgba(16, 185, 129, 0.06)" } }}
                          >
                            Add to spec
                          </Button>
                        ) : (
                          <Tooltip title="No matching candidate endpoint yet. The synthesizer drafts candidates hourly from observed traffic.">
                            <span>
                              <Button size="small" disabled sx={{ ...asecBtnTextSm, color: colors.neutral[400] }}>
                                Add to spec
                              </Button>
                            </span>
                          </Tooltip>
                        )
                      )}
                      {isShadow && canConfigure && <Box sx={{ color: colors.neutral[400] }}>|</Box>}
                      {canConfigure && (
                      <Button size="small" sx={asecBtnTextSm}
                        onClick={() => setReviewState([r.id], "saved")}>Mark reviewed</Button>
                      )}
                      {canConfigure && <Box sx={{ color: colors.neutral[400] }}>|</Box>}
                      {canConfigure && (
                      <Button size="small" sx={{ ...asecBtnTextSm, color: colors.neutral[700], "&:hover": { backgroundColor: colors.neutral[50] } }}
                        onClick={() => setReviewState([r.id], "ignored")}>Ignore</Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            sx={{
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
fontSize: 14, color: colors.neutral[700],
              },
            }}
          />
        </TableContainer>
      )}

      <MismatchDetailDialog
        open={!!mismatchDetail}
        info={mismatchDetail}
        onClose={() => setMismatchDetail(null)}
        onResolve={resolveDriftRow}
      />

      <EndpointDetailDrawer
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        row={drawerRow}
        driftRow={drawerRow ? driftByKey[`${drawerRow.host}|${drawerRow.method}|${drawerRow.path_template}`] : null}
        candidateEndpointId={drawerRow ? candEpByKey[`${drawerRow.host}|${drawerRow.method}|${drawerRow.path_template}`] : null}
        hasActiveSpec={hasActiveSpec}
        closedWorld={specPolicy.closed_world}
        onMarkReviewed={(ids) => { setReviewState(ids, "saved"); setDrawerRow(null); }}
        onIgnore={(ids) => { setReviewState(ids, "ignored"); setDrawerRow(null); }}
        onPromote={(id, host, method, path) => { promoteShadow(id, host, method, path); setDrawerRow(null); }}
        canConfigure={canConfigure}
        canWrite={canWrite}
      />
    </Stack>
  );
}

export default function AsecDiscovery() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  return (
    <AsecPage featureKey="api.openapi_validation"
      helpTooltip="Endpoints inferred from observed traffic. The path templater collapses variable segments (/users/123 -> /users/{id}) so similar URLs group as one endpoint. Default view filters to Needs Review."
      title="Discovery"
      helmetTitle="API Security — Discovery"
      pageDescription={(
        <>
          Automatically discover your application&apos;s web assets to map out your attack surface area and receive additional insights.
          <SectionHelp
            tooltip="Discovery is always on per site. Every request through Layerd is recorded into the catalog; the path templater collapses variable segments (/users/123 → /users/{id}) so similar URLs group as one endpoint. Click any row to see full detail. No traffic is sent for inspection — discovery is observability, not enforcement."
          />
        </>
      )}
      onRefresh={() => setRefreshKey((k) => k + 1)}
    >
      {(siteId) => <Body key={`${siteId}-${refreshKey}`} siteId={siteId} />}
    </AsecPage>
  );
}
